/* eslint-disable camelcase */
/* Município pelo nome, gravando o código IBGE (TI-2026-0358, RF01/RF02).
   A pessoa digita parte do nome (sem acento, sem caixa) ou o próprio código de 7 dígitos;
   o formulário guarda só o IBGE, que é o que vai na DPS. O backend confere o código
   contra a tabela do IBGE antes de salvar (RN17). */
import React, { useEffect, useMemo, useState } from 'react';
import { FormControl, FormErrorMessage, FormLabel, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { AsyncSelect } from 'chakra-react-select';
import _ from 'lodash';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useNfseEmissao } from '../../../../../hooks/useNfseEmissao';
import { INfseMunicipio } from '../../../../../types/nfseEmissao';
import { extractErrorMessage } from '../../../../../utils/ErrorUtils';
import { propsMenuSelect } from '../../../../../utils/selectMenu';
import { C } from './ui';

interface Opcao {
  value: string;
  label: string;
  municipio: INfseMunicipio;
}

const opcao = (m: INfseMunicipio): Opcao => ({
  value: m.codigo_ibge,
  label: `${m.nome}/${m.uf} · ${m.codigo_ibge}`,
  municipio: m,
});

export function MunicipioBusca({
  name,
  label,
  isRequired,
  isDisabled,
  helper,
  helperColor,
  sugestao,
  controlMb,
}: {
  name: string;
  label: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  helper?: string;
  helperColor?: string;
  // Nome que não casou com o IBGE (ex.: vindo da consulta do CNPJ): fica digitado na
  // busca, com as opções já carregadas, para a pessoa só escolher (RF03).
  sugestao?: { nome: string; uf?: string | null } | null;
  controlMb?: number;
}) {
  const { buscarMunicipios } = useNfseEmissao();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = _.get(errors, name);
  const codigo = (useWatch({ name }) as string) ?? '';
  const [texto, setTexto] = useState('');
  const [sugeridas, setSugeridas] = useState<Opcao[] | boolean>(false);
  // O último município escolhido nesta sessão; evita buscar o nome de novo.
  const [escolhido, setEscolhido] = useState<INfseMunicipio | null>(null);

  // Código já gravado (rascunho, padrão do emitente, consulta do CNPJ): busca o nome.
  const conhecido = escolhido?.codigo_ibge === codigo ? escolhido : null;
  const nomeDoCodigo = useQuery({
    queryKey: ['nfse-municipio', codigo],
    queryFn: () => buscarMunicipios(codigo),
    enabled: /^\d{7}$/.test(codigo) && !conhecido,
    staleTime: Infinity,
  });
  const atual = conhecido ?? nomeDoCodigo.data?.find(m => m.codigo_ibge === codigo) ?? null;

  const carregar = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    return (termo: string): Promise<Opcao[]> =>
      new Promise(resolve => {
        clearTimeout(timer);
        if (termo.trim().length < 2) {
          resolve([]);
          return;
        }
        timer = setTimeout(() => {
          buscarMunicipios(termo)
            .then(lista => resolve(lista.map(opcao)))
            .catch(() => resolve([]));
        }, 250);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sugestao?.nome) return;
    setTexto(sugestao.nome);
    buscarMunicipios(sugestao.nome, sugestao.uf ?? undefined)
      .then(lista => setSugeridas(lista.map(opcao)))
      .catch(() => setSugeridas(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sugestao?.nome, sugestao?.uf]);

  let ajuda = helper;
  if (!ajuda && codigo && !atual && nomeDoCodigo.isFetched) {
    ajuda = `O código ${codigo} não é um município do IBGE: escolha na lista`;
  }

  return (
    <FormControl isRequired={isRequired} isInvalid={!!error} mb={controlMb ?? 5}>
      <FormLabel fontWeight="bold" mb={1} htmlFor={`${name}-busca`}>
        {label}
      </FormLabel>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, onBlur } }) => (
          <AsyncSelect<Opcao>
            inputId={`${name}-busca`}
            placeholder="Digite o nome do município"
            loadOptions={carregar}
            defaultOptions={sugeridas}
            inputValue={texto}
            onInputChange={(novo, { action }) => {
              if (action === 'input-change') setTexto(novo);
              if (action === 'input-blur' || action === 'set-value') setTexto('');
            }}
            value={atual ? opcao(atual) : null}
            onChange={item => {
              const m = (item as Opcao | null)?.municipio ?? null;
              setEscolhido(m);
              setSugeridas(false);
              onChange(m?.codigo_ibge ?? '');
            }}
            onBlur={onBlur}
            loadingMessage={() => 'Buscando...'}
            noOptionsMessage={({ inputValue }) =>
              inputValue.trim().length < 2 ? 'Digite o nome ou o código IBGE' : 'Nenhum município'
            }
            isClearable
            isDisabled={isDisabled}
            isInvalid={!!error}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
            {...propsMenuSelect}
          />
        )}
      />
      {ajuda && !error && (
        <Text fontSize="12px" mt="6px" color={helperColor ?? C.subtle}>
          {ajuda}
        </Text>
      )}
      <FormErrorMessage>{extractErrorMessage(error?.message)}</FormErrorMessage>
    </FormControl>
  );
}
